import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Spinner, Empty, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { RefreshCw, Plus, ShoppingBag, AlertTriangle, Package } from "lucide-react";

const CORES = ["Amarela", "Branca", "Azul", "Preta", "Dourada"];
const TIPOS = ["Genérica", "Serenata", "Usada", "Roxa"];

const corSwatch = {
  Amarela: "bg-yellow-400",
  Branca:  "bg-white border-2 border-ink-300",
  Azul:    "bg-blue-500",
  Preta:   "bg-black",
  Dourada: "bg-amber-600",
};

const LIMITE_BAIXO = 100; // aviso se < 100 sacarias

export default function SacariasEstoque() {
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sacarias/stock");
      setStock(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Layout title="Estoque de Sacaria"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  const combos = stock?.combinations || [];
  const items = stock?.items || [];
  const totalDisp = stock?.total_disponivel || 0;
  const totalValor = items.reduce((a, i) => a + (i.valor_estoque || 0), 0);
  const alertas = combos.filter((c) => c.disponivel > 0 && c.disponivel < LIMITE_BAIXO);
  const zerados = combos.filter((c) => c.disponivel === 0 && c.comprado > 0);

  // Matrix: linhas = tipos, colunas = cores
  const matrix = TIPOS.map((tipo) => ({
    tipo,
    cores: CORES.map((cor) => combos.find((c) => c.tipo === tipo && c.cor === cor) || { tipo, cor, disponivel: 0, comprado: 0, consumido: 0, preco_medio: 0, valor_estoque: 0 }),
  }));

  return (
    <Layout
      title="Estoque de Sacaria"
      subtitle="Visão consolidada por tipo e cor"
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} data-testid="reload-btn"><RefreshCw size={16} /> Atualizar</Button>
          <Link to="/sacarias"><Button data-testid="new-purchase-btn"><Plus size={16} /> Nova compra</Button></Link>
        </div>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Cards resumo geral */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 flex items-center gap-1.5"><ShoppingBag size={12} /> Total em estoque</div>
          <div className="font-mono text-3xl font-semibold text-ink-900 mt-1">{fmtNum(totalDisp, 0)}</div>
          <div className="text-xs text-ink-500">sacarias disponíveis</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Valor em estoque</div>
          <div className="font-mono text-2xl font-semibold text-brand-500 mt-1">{fmtBRL(totalValor)}</div>
          <div className="text-xs text-ink-500">baseado no preço médio</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-emerald-700">Genérica</div>
          <div className="font-mono text-2xl font-semibold text-emerald-700 mt-1">{fmtNum(items.find((i) => i.tipo === "Genérica")?.disponivel || 0, 0)}</div>
          <div className="text-xs text-ink-500">total disponível</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-brand-500">Serenata</div>
          <div className="font-mono text-2xl font-semibold text-brand-500 mt-1">{fmtNum(items.find((i) => i.tipo === "Serenata")?.disponivel || 0, 0)}</div>
          <div className="text-xs text-ink-500">total disponível</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-amber-700">Usada</div>
          <div className="font-mono text-2xl font-semibold text-amber-700 mt-1">{fmtNum(items.find((i) => i.tipo === "Usada")?.disponivel || 0, 0)}</div>
          <div className="text-xs text-ink-500">total disponível</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-purple-700">Roxa</div>
          <div className="font-mono text-2xl font-semibold text-purple-700 mt-1">{fmtNum(items.find((i) => i.tipo === "Roxa")?.disponivel || 0, 0)}</div>
          <div className="text-xs text-ink-500">total disponível</div>
        </Card>
      </div>

      {/* Alertas */}
      {(alertas.length > 0 || zerados.length > 0) && (
        <Card className="p-4 mb-6 border-orange-200 bg-orange-50/40">
          <div className="flex items-center gap-2 mb-2 text-orange-700 font-semibold">
            <AlertTriangle size={18} /> Atenção — estoque baixo ou zerado
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {alertas.map((c) => (
              <div key={`al-${c.tipo}-${c.cor}`} className="flex items-center gap-2 text-orange-700">
                <span className={`inline-block w-3 h-3 rounded-full ${corSwatch[c.cor] || "bg-stone-300"}`}></span>
                <span>{c.tipo} {c.cor}: <strong>{fmtNum(c.disponivel, 0)}</strong> restantes (abaixo de {LIMITE_BAIXO})</span>
              </div>
            ))}
            {zerados.map((c) => (
              <div key={`ze-${c.tipo}-${c.cor}`} className="flex items-center gap-2 text-danger">
                <span className={`inline-block w-3 h-3 rounded-full ${corSwatch[c.cor] || "bg-stone-300"}`}></span>
                <span>{c.tipo} {c.cor}: <strong>0</strong> — estoque esgotado (já consumiu {fmtNum(c.consumido, 0)} de {fmtNum(c.comprado, 0)})</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Matriz tipo × cor */}
      {matrix.every((m) => m.cores.every((c) => c.comprado === 0)) ? (
        <Card><Empty title="Sem sacarias cadastradas" hint="Cadastre uma compra em Sacarias para começar a controlar o estoque." /></Card>
      ) : (
        <div className="space-y-6">
          {matrix.map((row) => (
            <Card key={row.tipo} className="p-5" data-testid={`sacaria-tipo-${row.tipo.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-ink-900 text-lg flex items-center gap-2">
                  <Package size={18} className="text-brand-500" /> Sacaria {row.tipo}
                </h3>
                <div className="text-sm text-ink-500">
                  Total: <span className="font-mono font-bold text-ink-900">{fmtNum(row.cores.reduce((a, c) => a + c.disponivel, 0), 0)}</span> sacarias
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {row.cores.map((c) => {
                  const empty = c.comprado === 0;
                  const isLow = c.disponivel > 0 && c.disponivel < LIMITE_BAIXO;
                  const isZero = c.disponivel === 0 && !empty;
                  const border = isZero ? "border-red-300" : isLow ? "border-orange-300" : empty ? "border-ink-200 opacity-40" : "border-ink-200";
                  const bg = isZero ? "bg-red-50/40" : isLow ? "bg-orange-50/40" : "bg-stone-50";
                  return (
                    <div key={c.cor} className={`border rounded-lg p-3 ${border} ${bg}`} data-testid={`sacaria-cell-${row.tipo}-${c.cor}`.toLowerCase()}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-block w-4 h-4 rounded-full ${corSwatch[c.cor]}`}></span>
                        <span className="font-bold text-sm text-ink-900">{c.cor}</span>
                      </div>
                      <div className="font-mono text-2xl font-bold leading-tight">
                        <span className={isZero ? "text-danger" : isLow ? "text-orange-600" : "text-ink-900"}>
                          {fmtNum(c.disponivel, 0)}
                        </span>
                      </div>
                      <div className="text-xs text-ink-500">disponíveis</div>
                      {c.comprado > 0 && (
                        <div className="mt-2 pt-2 border-t border-ink-200 space-y-0.5 text-xs">
                          <div className="flex justify-between text-ink-500"><span>Comprado</span><span className="font-mono">{fmtNum(c.comprado, 0)}</span></div>
                          <div className="flex justify-between text-ink-500"><span>Consumido</span><span className="font-mono">{fmtNum(c.consumido, 0)}</span></div>
                          {c.preco_medio > 0 && (
                            <div className="flex justify-between text-ink-500"><span>Preço médio</span><span className="font-mono">{fmtBRL(c.preco_medio)}</span></div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
}
